import type { TFunction } from 'i18next';
import type { Task } from '@/common/api/models/task';
import type { EvalDimRating } from '@/common/api/models/evalDimRating';
import type { HighlightSegment } from '@/common/api/models/highlightSegment';
import type { SubmissionEvaluationAnyOf } from '@/common/api/models/submissionEvaluationAnyOf';
import type { FlatTaskEvaluationInput } from '@/common/api/models/flatTaskEvaluationInput';
import type { OralExamTaskEvaluationInput } from '@/common/api/models/oralExamTaskEvaluationInput';
import type { OralExamTaskEvaluationOutput } from '@/common/api/models/oralExamTaskEvaluationOutput';
import { getOralExamQuestionTitle } from '@/common/components/OralExamQuestionText';
import { getFlatEvaluationDimensions } from '@/common/utils/evaluation/getFlatEvaluationDimensions';
import type { OralExamSubEvaluationOutput } from '@/common/api/models/oralExamSubEvaluationOutput';
import type { OralExamSubEvaluationRatingOutput } from '@/common/api/models/oralExamSubEvaluationRatingOutput';
import log from '@/common/utils/logger';
import type {
  OralExamTaskFormEvaluation,
  TaskFormEvaluation,
} from '../types';

const DEFAULT_HIGHLIGHTING: Record<string, HighlightSegment[]> = {
  description: [],
  analysis: [],
  application: [],
  reflection: [],
};

export const ORAL_EXAM_FALLBACK_QUESTION_COUNT = 6;
/** Matches OralExamConfigForm MAX_QUESTION_COUNT */
export const ORAL_EXAM_MAX_QUESTION_COUNT = 50;
export const MANUAL_ORAL_EXAM_QUESTION_ID_PREFIX = 'manual-q';

export function isOralExamTask(task: Task): boolean {
  return task.isOralExam === true || task.type === 'oral_exam';
}

export function isOralExamTaskEvaluation(
  evaluation: SubmissionEvaluationAnyOf | undefined | null,
): evaluation is OralExamTaskEvaluationOutput {
  if (!evaluation || typeof evaluation !== 'object') {
    return false;
  }
  if ('kind' in evaluation && evaluation.kind === 'oral_exam') {
    return true;
  }
  return 'subEvaluations' in evaluation && !!evaluation.subEvaluations;
}

function emptyDimGrades(dimensionKeys: string[]): Record<string, EvalDimRating> {
  return Object.fromEntries(
    dimensionKeys.map((dim) => [dim, { level: null, comment: null }]),
  );
}

function uniqueOrderedKeys(keys: string[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  keys.forEach((key) => {
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    ordered.push(key);
  });
  return ordered;
}

function collectStoredOralDimensionKeys(task: Task): string[] {
  const evaluation = task.submission?.evaluation;
  if (!isOralExamTaskEvaluation(evaluation)) {
    return [];
  }
  return uniqueOrderedKeys(
    Object.values(evaluation.subEvaluations ?? {}).flatMap((sub) => Object.keys(sub?.dimGrades ?? {})),
  );
}

function collectFormOralDimensionKeys(formEval: OralExamTaskFormEvaluation): string[] {
  return uniqueOrderedKeys(
    Object.values(formEval.subEvaluations ?? {}).flatMap((sub) => Object.keys(sub?.dimGrades ?? {})),
  );
}

/**
 * Oral rating rows read the task payload, not tenant essay dims.
 * Prefer scoredDimensionIds (server allow-list), then keys already on
 * stored sub-evaluations, then tenant oralExamDimensionKeys.
 */
export function resolveOralExamDimensionKeys(
  task: Task,
  tenantOralKeys: string[] = [],
): string[] {
  const scored = uniqueOrderedKeys(task.scoredDimensionIds ?? []);
  if (scored.length > 0) {
    return scored;
  }
  const stored = collectStoredOralDimensionKeys(task);
  if (stored.length > 0) {
    return stored;
  }
  return uniqueOrderedKeys(tenantOralKeys);
}

function resolveOralKeysForSave(
  formEval: OralExamTaskFormEvaluation,
  oralExamDimensionKeys: string[],
): string[] {
  const configured = uniqueOrderedKeys(oralExamDimensionKeys);
  if (configured.length > 0) {
    return configured;
  }
  return collectFormOralDimensionKeys(formEval);
}

function normalizeDimRating(rating?: EvalDimRating | string | null): EvalDimRating {
  if (typeof rating === 'string') {
    return {
      level: rating === 'not_applicable' ? null : rating,
      comment: null,
    };
  }
  return {
    level: rating?.level === 'not_applicable' ? null : (rating?.level ?? null),
    comment: rating?.comment ?? null,
  };
}

function processDimGradesForSave(
  dimGrades: Record<string, EvalDimRating | undefined> | undefined,
  dimensionKeys: string[],
): Record<string, { level: string | null; comment: string | null }> {
  const result: Record<string, { level: string | null; comment: string | null }> = {};
  dimensionKeys.forEach((dim) => {
    const rating = dimGrades?.[dim];
    result[dim] = {
      level: rating?.level === 'not_applicable' ? null : (rating?.level ?? null),
      comment: rating?.comment ?? null,
    };
  });
  return result;
}

/** Keys marked systemManaged in resolved grading config (e.g. formal). */
export function deriveSystemManagedKeys(
  evalDimensionKeys: string[],
  dimensionMeta?: Record<string, { systemManaged?: boolean } | undefined>,
): string[] {
  if (!dimensionMeta) {
    return [];
  }
  return evalDimensionKeys.filter((key) => dimensionMeta[key]?.systemManaged === true);
}

function getOralExamTargetQuestionCount(task: Task): number {
  const configuredCount = task.oralExamConfig?.questionCount;
  if (
    typeof configuredCount === 'number'
    && Number.isFinite(configuredCount)
    && configuredCount > 0
  ) {
    return Math.min(Math.floor(configuredCount), ORAL_EXAM_MAX_QUESTION_COUNT);
  }
  return ORAL_EXAM_FALLBACK_QUESTION_COUNT;
}

function getOralExamPoolQuestionIds(task: Task): string[] {
  return (task.oralExamConfig?.questionPool ?? [])
    .map((question) => question.questionId)
    .filter((id): id is string => !!id);
}

function createManualOralExamQuestionId(index: number): string {
  return `${MANUAL_ORAL_EXAM_QUESTION_ID_PREFIX}${index}`;
}

export function getManualOralExamQuestionNumber(questionId: string): number | null {
  const match = questionId.match(/^manual-q(\d+)$/);
  if (!match) {
    return null;
  }
  return Number(match[1]);
}

const AUTOGRADER_ORAL_EXAM_QUESTION_KEY_PATTERN = /^Q(\d+)$/;
type OralExamStoredSubEvaluation = OralExamSubEvaluationRatingOutput | OralExamSubEvaluationOutput;

export function parseAutograderOralExamQuestionIndex(questionKey: string): number | null {
  const match = questionKey.match(AUTOGRADER_ORAL_EXAM_QUESTION_KEY_PATTERN);
  if (!match) {
    return null;
  }
  return Number(match[1]);
}

function readOralExamSubEvaluationsRecord(task: Task): Record<string, OralExamStoredSubEvaluation> {
  const { submission } = task;
  if (!submission) {
    return {};
  }

  const apiEvaluation = submission.evaluation;
  if (isOralExamTaskEvaluation(apiEvaluation) && apiEvaluation.subEvaluations) {
    const { subEvaluations } = apiEvaluation;
    if (Array.isArray(subEvaluations)) {
      return Object.fromEntries(
        subEvaluations
          .filter((row): row is OralExamSubEvaluationOutput => !!row?.id)
          .map((row) => [row.id, row]),
      );
    }
    return subEvaluations;
  }

  const { oralExamEvaluation } = submission;
  if (oralExamEvaluation?.subEvaluations?.length) {
    return Object.fromEntries(
      oralExamEvaluation.subEvaluations.map((row) => [row.id, row]),
    );
  }

  return {};
}

function collectLegacyStoredOralEvalKeys(task: Task): string[] {
  return uniqueOrderedKeys(Object.keys(readOralExamSubEvaluationsRecord(task)));
}

/**
 * Join stored oral ratings onto asked row ids using the bind ladder:
 * exact asked id → Qn/qn at draw index → warn+order zip of non-pool leftovers.
 * Leftover bank pool ids that were not asked are dropped.
 */
export function bindOralExamSubEvaluationsToAskedIds(
  task: Task,
  askedIds: string[],
  storedByKey: Record<string, OralExamStoredSubEvaluation>,
): Map<string, OralExamStoredSubEvaluation> {
  const poolIds = new Set(getOralExamPoolQuestionIds(task));
  const result = new Map<string, OralExamStoredSubEvaluation>();
  const usedStoredKeys = new Set<string>();

  askedIds.forEach((askedId) => {
    const sub = storedByKey[askedId];
    if (sub) {
      result.set(askedId, sub);
      usedStoredKeys.add(askedId);
    }
  });

  askedIds.forEach((askedId, index) => {
    if (result.has(askedId)) {
      return;
    }
    const turnKeys = [`Q${index + 1}`];
    const lowerTurnKey = `q${index + 1}`;
    if (!poolIds.has(lowerTurnKey) && storedByKey[lowerTurnKey]) {
      turnKeys.push(lowerTurnKey);
    }
    const matchedTurnKey = turnKeys.find((key) => storedByKey[key]);
    if (matchedTurnKey) {
      result.set(askedId, storedByKey[matchedTurnKey]);
      usedStoredKeys.add(matchedTurnKey);
    }
  });

  const unboundAsked = askedIds.filter((askedId) => !result.has(askedId));
  const nonPoolLeftovers = Object.keys(storedByKey).filter(
    (key) => !usedStoredKeys.has(key) && !poolIds.has(key),
  );

  const zipCount = Math.min(nonPoolLeftovers.length, unboundAsked.length);
  if (nonPoolLeftovers.length > unboundAsked.length && zipCount > 0) {
    log.warn(
      'Oral exam eval bind: more non-pool stored rating keys than unbound asked ids; extras ignored',
      {
        taskId: task.id,
        unboundAskedCount: unboundAsked.length,
        leftoverCount: nonPoolLeftovers.length,
      },
    );
  }

  for (let index = 0; index < zipCount; index += 1) {
    const askedId = unboundAsked[index];
    const storedKey = nonPoolLeftovers[index];
    result.set(askedId, storedByKey[storedKey]);
    usedStoredKeys.add(storedKey);
  }

  return result;
}

function readStringIdArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
}

function readFilledQuestionsFromRecord(record: unknown): Record<string, string> {
  if (!record || typeof record !== 'object') {
    return {};
  }
  const candidate = record as Record<string, unknown>;
  const raw = candidate.filledQuestions ?? candidate.filled_questions;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      (entry): entry is [string, string] => typeof entry[0] === 'string' && typeof entry[1] === 'string',
    ),
  );
}

function readOralExamSessionFromTask(task: Task) {
  return task.submission?.oralExamSession;
}

function readOralExamSessionQuestions(task: Task) {
  return readOralExamSessionFromTask(task)?.questions ?? [];
}

function readOralExamSessionQuestionIds(task: Task): string[] {
  return readStringIdArray(readOralExamSessionFromTask(task)?.selectedQuestionIds);
}

function readFilledQuestionText(task: Task, poolQuestionId: string): string | undefined {
  const sessionFilled = readOralExamSessionFromTask(task)?.filledQuestions?.[poolQuestionId];
  if (sessionFilled) {
    return sessionFilled;
  }

  const submission = task.submission as Record<string, unknown> | null | undefined;
  if (!submission) {
    return undefined;
  }

  const sealedSummary = submission.oralExamSealedSummary ?? submission.oral_exam_sealed_summary;
  const sealedText = readFilledQuestionsFromRecord(sealedSummary)[poolQuestionId];
  if (sealedText) {
    return sealedText;
  }

  const run = submission.oralExamRun ?? submission.oral_exam_run;
  return readFilledQuestionsFromRecord(run)[poolQuestionId];
}
function readSelectedQuestionIdsFromRecord(record: unknown): string[] {
  if (!record || typeof record !== 'object') {
    return [];
  }
  const candidate = record as Record<string, unknown>;
  return readStringIdArray(
    candidate.selectedQuestionIds ?? candidate.selected_question_ids,
  );
}

/**
 * Resolve the question ids drawn for this oral exam session from submission metadata.
 * Prefer the sealed summary (post-exam grading snapshot), then the live run embed.
 */
export function getOralExamSelectedQuestionIdsFromTask(task: Task): string[] {
  const submission = task.submission as Record<string, unknown> | null | undefined;
  if (!submission) {
    return [];
  }

  const sealedSummary = submission.oralExamSealedSummary ?? submission.oral_exam_sealed_summary;
  const sealedIds = readSelectedQuestionIdsFromRecord(sealedSummary);
  if (sealedIds.length > 0) {
    return sealedIds;
  }

  const run = submission.oralExamRun ?? submission.oral_exam_run;
  return readSelectedQuestionIdsFromRecord(run);
}

export function resolveOralExamPoolQuestionId(task: Task, questionKey: string): string | undefined {
  const autograderIndex = parseAutograderOralExamQuestionIndex(questionKey);
  if (autograderIndex != null) {
    const selectedQuestionIds = getOralExamSelectedQuestionIdsFromTask(task);
    return selectedQuestionIds[autograderIndex - 1];
  }
  return questionKey;
}

function resolveOralExamSessionQuestionId(task: Task, questionKey: string): string {
  return resolveOralExamPoolQuestionId(task, questionKey) ?? questionKey;
}

/**
 * Per-question transcript chapter from GET oralExamSession.questions.
 * Returns a string (including empty), null when explicitly absent, or undefined when unmatched.
 */
export function resolveOralExamQuestionTranscriptHtml(
  task: Task,
  questionKey: string,
): string | null | undefined {
  const sessionQuestionId = resolveOralExamSessionQuestionId(task, questionKey);
  const sessionQuestion = readOralExamSessionQuestions(task).find(
    (question) => question.id === sessionQuestionId,
  );
  if (!sessionQuestion) {
    return undefined;
  }
  const { transcriptHtml } = sessionQuestion;
  if (typeof transcriptHtml === 'string' || transcriptHtml === null) {
    return transcriptHtml;
  }
  return undefined;
}

/**
 * Hide the top-of-task full transcript blob only when every asked question has a string chapter.
 */
export function shouldHideOralExamSubmissionBlob(task: Task): boolean {
  const askedIds = readOralExamSessionQuestionIds(task);
  if (askedIds.length === 0) {
    return false;
  }
  const questionsById = new Map(
    readOralExamSessionQuestions(task).map((question) => [question.id, question]),
  );
  return askedIds.every((askedId) => typeof questionsById.get(askedId)?.transcriptHtml === 'string');
}

export function resolveOralExamQuestionTextFromTask(
  task: Task,
  questionKey: string,
): string | undefined {
  const poolQuestionId = resolveOralExamPoolQuestionId(task, questionKey);
  if (!poolQuestionId) {
    return undefined;
  }
  const overlayText = readFilledQuestionText(task, poolQuestionId);
  if (overlayText) {
    return overlayText;
  }
  return task.oralExamConfig?.questionPool?.find(
    (question) => question.questionId === poolQuestionId,
  )?.question;
}

export interface OralExamQuestionDisplay {
  headerTitle: string;
  fullQuestionText: string | null;
}

export function resolveOralExamQuestionDisplay(
  task: Task,
  questionKey: string,
  t: TFunction,
): OralExamQuestionDisplay {
  const fullQuestionText = resolveOralExamQuestionTextFromTask(task, questionKey) ?? null;

  if (fullQuestionText) {
    const shortTitle = getOralExamQuestionTitle(fullQuestionText);
    return {
      headerTitle: shortTitle
        ?? fullQuestionText.split('\n')[0]?.trim()
        ?? fullQuestionText,
      fullQuestionText,
    };
  }

  const manualQuestionNumber = getManualOralExamQuestionNumber(questionKey);
  if (manualQuestionNumber != null) {
    return {
      headerTitle: t('lecturer.evalDesk.oralExam.questionFallbackTitle', { id: manualQuestionNumber }),
      fullQuestionText: null,
    };
  }

  const autograderIndex = parseAutograderOralExamQuestionIndex(questionKey);
  if (autograderIndex != null) {
    return {
      headerTitle: t('lecturer.evalDesk.oralExam.questionFallbackTitle', { id: autograderIndex }),
      fullQuestionText: null,
    };
  }

  return {
    headerTitle: t('lecturer.evalDesk.oralExam.questionFallbackTitle', { id: questionKey }),
    fullQuestionText: null,
  };
}

export function getDefaultOralExamQuestionIds(task: Task): string[] {
  const targetQuestionCount = getOralExamTargetQuestionCount(task);
  const poolQuestionIds = getOralExamPoolQuestionIds(task).slice(0, targetQuestionCount);

  if (poolQuestionIds.length >= targetQuestionCount) {
    return poolQuestionIds;
  }

  const manualQuestionCount = Math.max(0, targetQuestionCount - poolQuestionIds.length);
  if (manualQuestionCount === 0) {
    return poolQuestionIds;
  }

  const manualQuestionIds = Array.from(
    { length: manualQuestionCount },
    (_, index) => createManualOralExamQuestionId(poolQuestionIds.length + index + 1),
  );
  return [...poolQuestionIds, ...manualQuestionIds];
}

export function getOralExamQuestionIds(task: Task): string[] {
  const session = readOralExamSessionFromTask(task);
  if (session != null) {
    return readOralExamSessionQuestionIds(task);
  }
  return collectLegacyStoredOralEvalKeys(task);
}

export function buildTaskFormEvaluationFromApi(
  task: Task,
  evalDimensionKeys: string[],
  oralExamDimensionKeys: string[],
): TaskFormEvaluation {
  const { submission } = task;
  const apiEvaluation = submission?.evaluation as SubmissionEvaluationAnyOf | null | undefined;

  if (isOralExamTask(task)) {
    const oralEval = isOralExamTaskEvaluation(apiEvaluation) ? apiEvaluation : null;
    const questionIds = getOralExamQuestionIds(task);
    const dimensionKeys = resolveOralExamDimensionKeys(task, oralExamDimensionKeys);
    const storedByKey = readOralExamSubEvaluationsRecord(task);
    const boundSubEvaluations = bindOralExamSubEvaluationsToAskedIds(task, questionIds, storedByKey);
    const subEvaluations: OralExamTaskFormEvaluation['subEvaluations'] = {};

    questionIds.forEach((questionId) => {
      const sub = boundSubEvaluations.get(questionId);
      const dimGrades = emptyDimGrades(dimensionKeys);
      dimensionKeys.forEach((dim) => {
        dimGrades[dim] = normalizeDimRating(sub?.dimGrades?.[dim]);
      });
      subEvaluations[questionId] = {
        dimGrades,
        questionFeedback: sub?.questionFeedback ?? null,
      };
    });

    return {
      kind: 'oral_exam',
      subEvaluations,
      overallFeedback: {
        text: oralEval?.overallFeedback?.text ?? '<p><br></p>',
        highlighting: null,
      },
      promptVersion: oralEval?.promptVersion ?? null,
      evalTs: submission?.modifiedAt ?? null,
    };
  }

  const flatDimensions = getFlatEvaluationDimensions(apiEvaluation);
  const validatedDimensions: Record<string, EvalDimRating> = {};

  evalDimensionKeys.forEach((dim) => {
    if (flatDimensions[dim]) {
      validatedDimensions[dim] = normalizeDimRating(flatDimensions[dim]);
    }
  });

  const feedback = submission?.feedback as {
    text?: string;
    highlighting?: Record<string, HighlightSegment[]>;
  } | undefined;

  return {
    kind: 'flat',
    evaluation: {
      dimensions: validatedDimensions,
    },
    feedback: {
      text: feedback?.text || '<p><br></p>',
      highlighting: feedback?.highlighting || DEFAULT_HIGHLIGHTING,
    },
  };
}

export interface ModuleSaveTaskEvaluationPayload {
  evaluation: FlatTaskEvaluationInput | OralExamTaskEvaluationInput | null;
  feedback?: {
    text?: string | null;
    highlighting?: Record<string, HighlightSegment[]> | null;
  } | null;
}

export function formTaskEvaluationToModuleSavePayload(
  formEval: TaskFormEvaluation,
  evalDimensionKeys: string[],
  oralExamDimensionKeys: string[],
): ModuleSaveTaskEvaluationPayload {
  if (formEval.kind === 'oral_exam') {
    const dimensionKeys = resolveOralKeysForSave(formEval, oralExamDimensionKeys);
    const subEvaluations: OralExamTaskEvaluationInput['subEvaluations'] = {};
    Object.entries(formEval.subEvaluations).forEach(([questionId, sub]) => {
      subEvaluations[questionId] = {
        dimGrades: processDimGradesForSave(sub.dimGrades, dimensionKeys),
        questionFeedback: sub.questionFeedback ?? null,
      };
    });

    return {
      evaluation: {
        kind: 'oral_exam',
        subEvaluations,
        overallFeedback: {
          text: formEval.overallFeedback?.text ?? null,
          highlighting: null,
        },
        promptVersion: formEval.promptVersion ?? null,
      },
      feedback: null,
    };
  }

  const processedDimensions: Record<string, { level: string | null; comment: string | null }> = {};
  Object.entries(formEval.evaluation?.dimensions || {}).forEach(([dim, rating]) => {
    if (evalDimensionKeys.includes(dim)) {
      processedDimensions[dim] = {
        level: rating?.level === 'not_applicable' ? null : (rating?.level ?? null),
        comment: rating?.comment ?? null,
      };
    }
  });

  const rawFeedback = formEval.feedback || {};
  const processedHighlighting: Record<string, HighlightSegment[]> = {};
  if (rawFeedback.highlighting && typeof rawFeedback.highlighting === 'object') {
    Object.keys(rawFeedback.highlighting).forEach((dim) => {
      if (Array.isArray(rawFeedback.highlighting![dim])) {
        processedHighlighting[dim] = rawFeedback.highlighting![dim].map((h) => ({
          start: h.start,
          end: h.end,
        }));
      }
    });
  }

  return {
    evaluation: Object.keys(processedDimensions).length > 0
      ? { kind: 'flat', dimensions: processedDimensions }
      : null,
    feedback: {
      text: rawFeedback.text || null,
      highlighting: Object.keys(processedHighlighting).length > 0 ? processedHighlighting : null,
    },
  };
}

export interface AutosaveEvaluationPayload {
  evaluation: FlatTaskEvaluationInput | OralExamTaskEvaluationInput;
  feedback?: {
    text?: string | null;
    highlighting?: Record<string, HighlightSegment[]> | null;
  };
}

export function formTaskEvaluationToAutosavePayload(
  formEval: TaskFormEvaluation,
  evalDimensionKeys: string[],
  oralExamDimensionKeys: string[],
  applicableDims: string[],
  systemManagedKeys: string[] = [],
): AutosaveEvaluationPayload {
  const modulePayload = formTaskEvaluationToModuleSavePayload(
    formEval,
    evalDimensionKeys,
    oralExamDimensionKeys,
  );

  if (formEval.kind === 'oral_exam') {
    return {
      evaluation: modulePayload.evaluation as OralExamTaskEvaluationInput,
    };
  }

  const highlighting = { ...(formEval.feedback?.highlighting || DEFAULT_HIGHLIGHTING) };
  applicableDims.forEach((dim) => {
    if (!systemManagedKeys.includes(dim) && !highlighting[dim]) {
      highlighting[dim] = [];
    }
  });

  return {
    evaluation: (modulePayload.evaluation ?? { kind: 'flat', dimensions: {} }) as FlatTaskEvaluationInput,
    feedback: {
      text: formEval.feedback?.text || '',
      highlighting,
    },
  };
}

export interface ResolveModuleGradingFormDefaultsOptions {
  /** When true, display the persisted API record (graded submission). Auto-calc is not applicable. */
  finalized?: boolean;
}

/**
 * Derive module grading form defaults from server state.
 *
 * - **Draft** (`finalized: false`): auto-calc mode ignores stale server grades and
 *   recomputes live from task evaluations; manual override restores the saved grade.
 * - **Finalized** (`finalized: true`): show the persisted module grade as-is.
 */
export function resolveModuleGradingFormDefaults(
  grading: ({ comment?: string; grade?: string | number; manualGrade?: boolean | null } | null | undefined),
  defaultFeedback: string,
  options?: ResolveModuleGradingFormDefaultsOptions,
) {
  const feedback = grading?.comment || defaultFeedback;

  if (options?.finalized) {
    return {
      feedback,
      grade: grading?.grade != null ? Number(grading.grade).toFixed(1) : '',
      autoCalculateGrade: false,
    };
  }

  const manualGrade = grading?.manualGrade === true;
  const serverGrade = manualGrade && grading?.grade != null
    ? Number(grading.grade).toFixed(1)
    : '';

  return {
    feedback,
    grade: serverGrade,
    autoCalculateGrade: !manualGrade,
  };
}
